import React from 'react';
import { SEO } from '../components/SEO';
import { useCurrentUser } from '../contexts/AuthContext';
import { OrgCard, AddOrgCard } from '../components/shell/OrgCard';
import { EventCard } from '../components/shell/EventCard';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { PulseSeverity } from '../types';
import { Megaphone, Search, Plus, Calendar as CalendarIcon, LayoutGrid, Shield, Sparkles } from 'lucide-react';
import { Spinner } from '../components/ui/Spinner';
import { useTenant } from '../contexts/TenantContext';
import { useQuery } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';

import { useNavigate } from 'react-router-dom';

export const GlobalPortal = () => {
  const { user, memberships, isLoading } = useCurrentUser();
  const { brand } = useTenant();
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Every node lives on the platform domain (orgsets.com) with the slug carried in
  // state/localStorage — path-routed, no per-org subdomains. Switching orgs is always
  // same-origin: record the target slug and let the router/tenant context resolve it.
  const handleSwitch = (slug: string) => {
    localStorage.setItem('active_org', slug);
    window.location.href = '/dashboard';
  };

  const { data: dashboardData } = useQuery({
    queryKey: ['user-dashboard'],
    queryFn: async () => {
      const { data } = await apiWorker.get('/api/users/me/dashboard');
      return data;
    }
  });

  const urgentAnnouncements = dashboardData?.urgentAnnouncements?.map((a: any) => ({
    ...a,
    content: a.content.replace('System', brand.name),
    nodeName: a.nodeName === 'Global' ? `${brand.name} Global` : a.nodeName
  })) || [];

  const upcomingEvents = dashboardData?.upcomingEvents?.map((e: any) => ({
    ...e,
    date: new Date(e.date)
  })) || [];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 font-medium">Loading your organizations...</p>
      </div>
    );
  }

  if (memberships.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-white rounded-none flex items-center justify-center text-gray-300 mb-6 dark:bg-slate-800">
          <LayoutGrid size={40} />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">You haven't joined any organizations yet</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-8">
          {brand.name} is better with a team. Join an existing organization or create your own to get started.
        </p>
        <div className="flex gap-3">
          <Button variant="primary" onClick={() => navigate('/create-org')}>
            <Plus size={18} />
            Create Organization
          </Button>
          <Button variant="secondary" onClick={() => navigate('/')}>
            <Search size={18} />
            Explore public orgs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
      <SEO title="All Organizations" noindex={true} />
      {/* Main Content */}
      <div className="xl:col-span-8 space-y-8">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl text-navy-900 dark:text-white mb-2">
              {getGreeting()}, {user?.firstName || 'there'}
            </h1>
            <p className="text-gray-500">Welcome back to your organization.</p>
          </div>
          {memberships.length > 1 && (
            <div className="flex gap-3 self-start md:self-auto">
              <Button 
                variant="primary" 
                className="gap-2 bg-navy-900 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-navy-900/10"
                onClick={() => handleSwitch('universal')}
              >
                <LayoutGrid size={18} />
                Universal Dashboard
              </Button>
            </div>
          )}
        </header>

        {/* Portability Kill Feature Announcement */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-none p-6 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-none blur-3xl dark:bg-slate-900" />
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
            <div className="p-4 bg-white/20 rounded-none backdrop-blur-md dark:bg-slate-900">
              <Shield size={32} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold font-display">New: Requirement Portability</h3>
              <p className="text-blue-100 text-sm mt-1 max-w-md italic">
                Background checks and safety certifications uploaded to one organization can now be "Passported" to any other chapter in seconds.
              </p>
            </div>
            <Button variant="secondary" className="bg-white text-blue-700 hover:bg-blue-50 border-none shrink-0 dark:bg-slate-900">
              Manage Passport
            </Button>
          </div>
        </section>

        {/* Aggregated Urgent Announcements */}
        {urgentAnnouncements.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-red-700 uppercase tracking-widest font-mono">
              <Megaphone size={14} />
              <span>Urgent Updates</span>
            </div>
            <div className="space-y-3">
              {urgentAnnouncements.map((ann: any) => (
                <Card key={ann.id} variant="announcement" severity={PulseSeverity.CRITICAL}>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-semibold text-red-900">{ann.title}</h3>
                    <span className="text-[10px] font-bold text-red-700/50 uppercase tracking-widest font-mono">
                      {ann.nodeName}
                    </span>
                  </div>
                  <p className="text-sm text-red-900/80 leading-relaxed">{ann.content}</p>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Discover Organizations</h2>
            <Badge variant="blue" className="bg-blue-50 text-blue-700 border-blue-100">Nearby</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:border-brand-primary transition-all group flex gap-4 items-center cursor-pointer dark:bg-slate-900">
              <div className="w-12 h-12 rounded-none bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-lg group-hover:scale-110 transition-transform">
                <Sparkles size={24} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-gray-900 dark:text-white">Downtown Biking Club</h4>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1">32 active members • Public Org</p>
                <div className="mt-2 flex items-center gap-1.5">
                  <Badge variant="gray" className="text-[8px] py-0">Fitness</Badge>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-brand-primary py-0 px-2 ml-auto">Request to Join</Button>
                </div>
              </div>
            </div>
            
            <div 
              onClick={() => navigate('/create-org')}
              className="p-5 bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white flex flex-col items-center justify-center text-center group cursor-pointer hover:bg-white dark:bg-slate-900">
              <Plus size={24} className="text-gray-300 group-hover:text-brand-primary mb-2" />
              <p className="text-xs font-bold text-gray-400 group-hover:text-gray-600">Start a new private Org</p>
            </div>
          </div>
        </section>

        {/* Organizations Grid */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your organizations</h2>
            <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
              View all
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {memberships.map((org: any) => (
              <OrgCard
                key={org.id}
                name={org.nodeName}
                slug={org.nodeSlug}
                role={org.role}
                memberCount={org.memberCount || 0}
                unreadCount={org.unreadCount}
                onClick={() => handleSwitch(org.nodeSlug)}
              />
            ))}
            <AddOrgCard onClick={() => navigate('/create-org')} />
          </div>
        </section>
      </div>

      {/* Sidebar - xl+ only */}
      <div className="hidden xl:block xl:col-span-4 space-y-8">
        <section>
          <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest font-mono mb-4">
            <CalendarIcon size={14} />
            <span>Coming up</span>
          </div>
          <div className="space-y-3">
            {upcomingEvents.map((event: any) => (
              <EventCard
                key={event.id}
                title={event.title}
                orgName={event.orgName}
                date={event.date}
              />
            ))}
            {upcomingEvents.length === 0 && (
              <div className="p-6 bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white text-center dark:bg-slate-950">
                <p className="text-xs text-gray-400">No upcoming events</p>
              </div>
            )}
          </div>
          <Button variant="ghost" className="w-full mt-4 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700" onClick={() => navigate('/calendar')}>
            View full calendar
          </Button>
        </section>

        <section className="bg-navy-900 rounded-none p-6 text-white overflow-hidden relative">
          <div className="relative z-10">
            <h3 className="font-display text-xl mb-2 text-white">Need a new space?</h3>
            <p className="text-sm text-blue-200 mb-6 leading-relaxed">
              Create a new organization node in seconds. Perfect for clubs, teams, or departments.
            </p>
            <Button 
              onClick={() => navigate('/create-org')}
              className="bg-white text-navy-900 dark:text-white hover:bg-blue-50 border-none w-full dark:bg-slate-900">
              Create Organization
            </Button>
          </div>
          {/* Decorative element */}
          <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-blue-600/20 rounded-none blur-2xl" />
        </section>
      </div>
    </div>
  );
};